import React from "react";
import { motion } from "framer-motion";
import { ExternalLink, Quote } from "lucide-react";
import { Overline } from "./Motifs";
import { PLAYFUL_BOOKS } from "../data/content";

const THEMES = ["Creativity", "Innovation", "Play", "Learning", "Experimentation"];

export default function PlayfulWorld() {
  return (
    <section
      id="playful"
      data-testid="playful-section"
      className="relative py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <Overline>Creating a More Playful World</Overline>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl text-[#1E2A4F] mt-5 leading-[0.95] tracking-tight">
              Play. <em className="italic font-light">Think. Build. Create.</em>
            </h2>
          </div>
          <div className="lg:col-span-4 text-[#5C6A8F] text-base sm:text-lg leading-relaxed">
            Two companion books for parents, educators, and quietly stubborn dreamers — and a
            small manifesto for raising humans who keep wondering.
          </div>
        </div>

        {/* Featured promo cards */}
        <div className="mt-16 grid lg:grid-cols-2 gap-8 lg:gap-10">
          {PLAYFUL_BOOKS.map((book, i) => (
            <motion.article
              key={book.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: i * 0.12 }}
              data-testid={`playful-book-${book.id}`}
              className="group rounded-lg overflow-hidden border border-[#E5DFD3] bg-white book-card"
            >
              <a
                href={book.purchase}
                target="_blank"
                rel="noreferrer"
                data-testid={`playful-book-${book.id}-purchase`}
                className="block"
              >
                <div className="aspect-square overflow-hidden bg-[#F3EDE4]">
                  <img
                    src={book.image}
                    alt={`${book.title} — ${book.label}`}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                  />
                </div>
                <div className="p-7 sm:p-8 border-t border-[#E5DFD3]">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <Overline>{book.label}</Overline>
                      <h3 className="font-display text-3xl sm:text-4xl text-[#1E2A4F] mt-3 leading-tight">
                        {book.title}
                      </h3>
                    </div>
                    <span className="shrink-0 mt-2 inline-flex items-center justify-center w-11 h-11 rounded-full border border-[#E5DFD3] text-[#1E2A4F] group-hover:bg-[#D95D39] group-hover:text-[#F9F6F0] group-hover:border-[#D95D39] transition-all duration-500">
                      <ExternalLink size={16} />
                    </span>
                  </div>
                  <p className="text-[15px] text-[#5C6A8F] leading-relaxed mt-5">{book.blurb}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-[#D95D39]">
                    Find on Amazon
                  </span>
                </div>
              </a>
            </motion.article>
          ))}
        </div>

        {/* Quote + Themes */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          <div className="md:col-span-7 bg-[#F3EDE4] rounded-lg p-10 sm:p-12 flex flex-col justify-between">
            <Quote className="text-[#D95D39]" size={28} />
            <p className="font-display text-2xl sm:text-3xl text-[#1E2A4F] italic leading-snug mt-4">
              &ldquo;Play is the highest form of research. We forgot that somewhere between Class Two
              and the boardroom.&rdquo;
            </p>
            <p className="text-xs tracking-[0.22em] uppercase text-[#5C6A8F] mt-6">— from Play</p>
          </div>

          <div className="md:col-span-5 bg-[#F9F6F0] border border-[#E5DFD3] rounded-lg p-10 sm:p-12">
            <Overline>Themes</Overline>
            <ul className="mt-6 space-y-3">
              {THEMES.map((t, idx) => (
                <li key={t} className="flex items-baseline gap-4">
                  <span className="font-display text-sm text-[#D95D39] tabular-nums">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <span className="font-display text-2xl sm:text-3xl text-[#1E2A4F]">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
